/**
 * Facade query that hydrates the Picks tab by fetching the two split endpoints
 * in parallel and merging them into the shape downstream hooks already consume.
 *
 *   /matches/static  → shared-across-users, Redis-backed on the server.
 *                      Polled every 60 s so live match scores stay current.
 *   /matches/me      → per-user picks / golden balls / futures.
 *                      Finite staleTime + refetch on focus so a settled `pointsEarned`
 *                      reconciles when the user returns to the screen (previously
 *                      staleTime: Infinity left settled scores stale until a mutation,
 *                      so a finished pick could sit on its pre-settlement state).
 *
 * The combined result shape stays identical to the pre-split version, so
 * setQueryData call sites in useWorldCupPickPersistence and useWorldCupPicksData
 * continue to work without modification to their consumers.
 */

import { useMemo, useRef } from 'react';
import { Platform } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { worldCupQueryKeys } from '../constants/worldcupQueryKeys';
import {
  GC_TIME_WORLD_CUP_MS,
  STALE_TIME_WORLD_CUP_MATCHES_MS,
} from '../constants/worldcupReactQuery.constants';
import {
  fetchWorldCupStaticMatches,
  fetchWorldCupUserMatchesOverlay,
  mergeWorldCupMatchesPayload,
} from '../helpers/worldCupMatchesApi.helper';
import type { WorldCupFuturesOptions } from '../helpers/worldcupPicksData.helper';

export function useWorldCupMatchesQuery(competitionId: string, enabled: boolean = true) {
  const apiClient = useApiClient();

  const staticQuery = useQuery({
    queryKey: worldCupQueryKeys.matchesStatic(competitionId),
    queryFn: () => fetchWorldCupStaticMatches(apiClient, competitionId),
    enabled,
    staleTime: STALE_TIME_WORLD_CUP_MATCHES_MS,
    gcTime: GC_TIME_WORLD_CUP_MS,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: Platform.OS === 'web',
  });

  const meQuery = useQuery({
    queryKey: worldCupQueryKeys.matchesMe(competitionId),
    queryFn: () => fetchWorldCupUserMatchesOverlay(apiClient, competitionId),
    enabled,
    staleTime: STALE_TIME_WORLD_CUP_MATCHES_MS,
    gcTime: GC_TIME_WORLD_CUP_MS,
    // Reconcile settled scores on app-resume / tab-focus (both platforms). Gated by
    // staleTime, so it only refires when actually stale — no per-render churn, and no
    // 60s per-user poll (the me-endpoint is per-user/Postgres-backed, unlike static).
    refetchOnWindowFocus: true,
  });

  // The winner-futures display (own card AND every leaderboard / profile row) resolves
  // its flag + name ONLY from `participantTeams`; with no id-derived fallback an empty
  // list blanks the winner everywhere. The server now guarantees a full list, but a
  // transient refetch can still surface an empty payload — retain the last options that
  // actually had teams so no consumer ever sees a blank winner ("refresh fixes it").
  const lastFuturesOptionsWithTeamsRef = useRef<WorldCupFuturesOptions | null>(null);
  const data = useMemo(() => {
    if (!staticQuery.data || !meQuery.data) return undefined;
    const merged = mergeWorldCupMatchesPayload(staticQuery.data, meQuery.data);
    const opts = merged.futuresOptions ?? null;
    if (opts && opts.participantTeams.length > 0) {
      lastFuturesOptionsWithTeamsRef.current = opts;
      return merged;
    }
    if (lastFuturesOptionsWithTeamsRef.current) {
      return { ...merged, futuresOptions: lastFuturesOptionsWithTeamsRef.current };
    }
    return merged;
  }, [staticQuery.data, meQuery.data]);

  return {
    data,
    isLoading: staticQuery.isLoading || meQuery.isLoading,
    isFetching: staticQuery.isFetching || meQuery.isFetching,
    isError: staticQuery.isError || meQuery.isError,
    error: staticQuery.error ?? meQuery.error,
  };
}
